import React, { useEffect, useRef, useCallback } from 'react';
import { LegacyColumn as Column } from 'components/MaterialTable/MatDataGridTypes';
import { useAuth } from '../../lib/Auth';
import { ServiceListFilter } from './ServiceListFilter';
import { Main } from 'styles/themeColors';
import { getAllServices } from 'requests/services';
import TableHeader from 'components/UI/MultiTableHeader';
import EditIcon from '@mui/icons-material/Edit';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { renderToast } from 'components/UI/ToastNotification';
import { GENERAL_CONSTANTS } from 'constants/general';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import MatDataGrid from 'components/MaterialTable/MatDataGrid';
import { createGlobalStyle } from 'styled-components';

const ServiceTableStyles = createGlobalStyle`
  .service-list-table-wrapper {
    /* Target Material React Table checkbox column selectors */
    .mrt-row-select,
    .mrt-table-head-cell-select,
    .mrt-table-body-cell-select,
    tbody tr td:first-child,
    thead tr th:first-child {
      padding: 8px 12px !important;
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      vertical-align: middle !important;
    }

    .mrt-row-select:has(input[type="checkbox"]),  
    .mrt-table-head-cell-select,  
    .mrt-table-body-cell-select,  
    table thead tr th:first-child, 
    table tbody tr td:first-child,  
    .MuiTableHead-root .MuiTableCell-head:first-child,  
    .MuiTableBody-root .MuiTableCell-body:first-child,  
    th[data-column-id="mrt-row-select"],  
    td[data-column-id="mrt-row-select"] {
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      padding: 4px !important;
    }
    
    /* Target Material-UI checkbox cells */
    .MuiTableCell-paddingCheckbox {
      padding: 8px 12px !important;
    }
    
    /* Target the actual checkbox component */
    .MuiCheckbox-root {
      padding: 4px !important;
      margin: 0 !important;
    }
    
    /* Additional fallback selectors */
    table tbody tr td:first-child,
    table thead tr th:first-child {
      padding: 8px 12px !important;
    }
    
    /* Material React Table specific class targeting */
    [class*="MuiTableCell"]:first-child {
      padding: 8px 12px !important;
    }
    
    /* Ensure all other data columns have consistent padding */
    table thead tr th:not(:first-child),
    table tbody tr td:not(:first-child) {
      padding: 16px 8px !important;
    }
    
    /* SKU column width - target second column when checkboxes are enabled */
    table thead tr th:nth-child(2),
    table tbody tr td:nth-child(2) {
      width: 200px !important;
      min-width: 200px !important;
      padding: 16px 8px !important;
    }
  }
`;

interface Props {
  selectedVendorId?: number;
  selectedBusinessUnitId?: number;
  filters: ServiceListFilter;
  searchTerm: string;
  serviceColumns: Column<any>[];
  setShowDeleteModal?: Function;
  setShowPublishModal?: Function;
  setSelectedRows?: Function;
  selectedRows: any[];
  reload?: boolean;
  setReload?: Function;
  setLoading?: Function;
  handleEdit?: Function;
  handleDelete?: Function;
  loading: boolean;
}

const ServiceListTable = ({ selectedVendorId,
  selectedBusinessUnitId,
  filters,
  searchTerm,
  serviceColumns,
  selectedRows,
  setSelectedRows,
  reload,
  setReload,
  setLoading,
  handleEdit,
  handleDelete,
  loading
}: Props) => {
  const { token } = useAuth();
  const [columns, setColumns] = React.useState(serviceColumns);
  const [tablesData, setTablesData] = React.useState<any>({
    data: [],
    page: 0,
    totalCount: 0,
  });
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(0);
  const [sortField, setSortField] = React.useState<string | undefined>();
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | undefined>();

  // Ref to track if component is mounted
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Setup columns with action handlers
  useEffect(() => {
    if (serviceColumns) {
      const actionColumn = {
        render: (rowData: any) => {
          return <FlexboxContainerComponent>
            <EditIcon style={{ cursor: 'pointer' }} onClick={() => handleEdit?.(rowData?.id)} htmlColor={Main.brandDark} />
          </FlexboxContainerComponent>
        }
      };

      const updatedColumns = serviceColumns.map((col) => {
        if (col.field === 'actions') {
          return { ...col, ...actionColumn };
        }
        // Apply defaultSort to the currently sorted column
        if (col.field === sortField && sortOrder) {
          return { ...col, defaultSort: sortOrder };
        }
        return { ...col, defaultSort: undefined };
      });

      setColumns(updatedColumns);
    }
  }, [serviceColumns, handleEdit, sortField, sortOrder]);

  // Build query params
  const buildQueryParams = useCallback(() => {
    let params = `page=${page + 1}&pageSize=${rowsPerPage}`;

    if (filters) {
      if (filters.publishStatus?.id) {
        params += `&statusId=${filters.publishStatus?.id}`;
      }
      if (filters.country && filters.country.length > 0) {
        params += `&countryId=${filters.country.join(',')}`;
      }
    }

    if (selectedVendorId) {
      params += `&vendorId=${selectedVendorId}`;
    }

    if (selectedBusinessUnitId) {
      params += `&businessUnitId=${selectedBusinessUnitId}`;
    }

    if (searchTerm && searchTerm.trim() !== '') {
      params += `&search=${searchTerm}`;
    }

    if (sortField && sortOrder) {
      params += `&sortField=${sortField}&sortOrder=${sortOrder}`;
    }

    return params;
  }, [page, rowsPerPage, filters, selectedVendorId, selectedBusinessUnitId, searchTerm, sortField, sortOrder]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading?.(true);
      const queryParams = buildQueryParams();
      const result = await getAllServices?.(queryParams, token);

      if (isMounted.current) {
        setTablesData({
          data: result.data,
          page: result.meta.currentPage - 1,
          totalCount: result.meta.totalRecords,
        });
        setReload?.(false);
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error('API error:', error);
        renderToast('error', error.message || `${GENERAL_CONSTANTS.FAILED_TO_FETCH} ${GENERAL_CONSTANTS.services}`);
      }
    } finally {
      setLoading?.(false);
    }
  }, [buildQueryParams, token, setLoading, setReload]);

  // Fetch data on dependency changes
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters, selectedVendorId, selectedBusinessUnitId, searchTerm, sortField, sortOrder, reload]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters, selectedVendorId, selectedBusinessUnitId, searchTerm]);

  const handleRowsPerPageChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1);
  };

  const handleSortChange = (orderBy: number, orderDirection: string) => {
    if (orderBy >= 0 && columns && columns[orderBy] && (orderDirection === 'asc' || orderDirection === 'desc')) {
      const field = String(columns[orderBy].field);
      setSortField(field);
      setSortOrder(orderDirection);
    }
  };

  const { readOnly } = useReadPermission(PermissionCode.ADDONS_CREATE);

  const totalPages = Math.ceil(tablesData.totalCount / rowsPerPage);

  return (
    <div className="service-list-table-wrapper">
      <ServiceTableStyles />
      <TableHeader handleDelete={handleDelete} selectedRows={selectedRows} readOnly={readOnly} />
      <MatDataGrid
        columns={columns}
        data={tablesData.data}
        isLoading={loading}
        sorting
        enableSelection
        onSelectionChange={(rows) => setSelectedRows?.(rows)}
        onOrderChange={handleSortChange}
        headerStyle={{
          color: '#FFF',
          fontSize: '16px',
          height: 50,
          padding: '0px'
        }}
        rowStyle={{
          fontSize: '14px',
          wordWrap: 'break-word',
        }}
        customPagination={{
          itemsPerPage: rowsPerPage,
          setItemsPerPage: handleRowsPerPageChange,
          currentPage: page + 1,
          setCurrentPage: handlePageChange,
          totalPages: totalPages > 0 ? totalPages : 1,
          baseRoute: 'services',
          shouldRedirect: false
        }}
      />
    </div>
  );
};

export default React.memo(ServiceListTable);
